<script setup lang="ts">
import { useProducts } from "@features/shopping/composables";
import { SOCIALS } from "@shared/constants/socials";
import { slugify } from "@shared/utils/slugify";
import { Separator } from "@ui/separator";
import { computed } from "vue";
import { RouterLink, useRoute } from "vue-router";

const { categories } = useProducts();
const route = useRoute();

const NAVIGATIONS = computed(() => [
  { name: "Home", path: "/" },
  ...categories.value.map((category) => ({
    name: category.product_cat_name,
    path: `/product-category/${slugify(category.product_cat_name)}`,
  })),
  { name: "My Account", path: "/my-account" },
  { name: "My Cart", path: "/cart" },
  { name: "FAQs", path: "/faqs" },
  { name: "Contact Us", path: "/contact-us" },
]);

const isActive = (url: string) => {
  if (url === "/") return route.path === "/";
  if (route.path.startsWith("/product-category/")) return route.path === url;
  return route.path.startsWith(url);
};
</script>

<template>
  <footer class="pt-[20px]">
    <div class="flex flex-col items-center justify-center text-center">
      <!-- Social Media Links -->
      <div class="mb-[20px] flex items-center justify-center gap-[30px]">
        <a
          v-for="social in SOCIALS"
          :key="social.platform"
          :href="social.url"
          class="group"
          :aria-label="`Follow on ${social.platform}`"
          target="_blank"
        >
          <img
            :src="social.icon"
            width="20"
            height="20"
            class="group-hover:hidden"
            :alt="`${social.platform} icon`"
          />
          <img
            :src="social.hoverIcon"
            width="20"
            height="20"
            class="hidden group-hover:block"
            :alt="`${social.platform} hover icon`"
          />
        </a>
      </div>

      <!-- Navigation Links -->
      <div class="mb-[30px] flex items-center justify-center gap-4 text-content lg:gap-10">
        <RouterLink
          to="/my-account"
          class="hover-through"
          :class="{ 'line-through': isActive('/my-account') }"
        >
          My Account
        </RouterLink>
        <Separator orientation="vertical" class="h-7" />
        <RouterLink
          to="/contact-us"
          class="hover-through"
          :class="{ 'line-through': isActive('/contact-us') }"
        >
          Contact Us
        </RouterLink>
      </div>

      <!-- Copyright Statement -->
      <span class="text-caption"> © MOLLY FACTORY 2020. </span>
    </div>

    <!-- Footer Navigation -->
    <div class="container mt-[45px] flex flex-col gap-[14px] pb-4 md:mt-[15px]">
      <RouterLink
        v-for="navigation in NAVIGATIONS"
        :key="navigation.name"
        :to="navigation.path"
        class="hover-through text-content"
        :class="{ 'line-through': isActive(navigation.path) }"
      >
        {{ navigation.name }}
      </RouterLink>
    </div>
  </footer>
</template>
